import clsx from 'clsx';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { doTrack } from '@/track/track.service';
import { Accordion, Icon } from '@seeqdev/qomponents';
import type { ComponentProps, ReactNode } from 'react';
import type { TooltipPosition } from '@seeqdev/qomponents';

export const AdvancedParameters = (props: {
  toolId: string;
  toolName: string;
  alternateName?: string;
  children?: ReactNode;
  iconStyle?: string;
  collapsed: boolean;
  onToggle?: (collapsed: boolean) => void;
}) => {
  const { toolName, alternateName = undefined, children, iconStyle, collapsed, onToggle } = props;
  const { t } = useTranslation();

  const trackAndClick = () => {
    doTrack(toolName, 'Advanced Toggle', `Advanced ${collapsed ? 'expanded' : 'collapsed'}`);
    onToggle?.(!collapsed);
  };

  return (
    <AdvancedSection
      collapsed={collapsed}
      alternateName={alternateName}
      trackAndClick={trackAndClick}
      t={t}
      iconStyle={iconStyle}>
      {children}
    </AdvancedSection>
  );
};

export type AdvancedParametersProps = ComponentProps<typeof AdvancedParameters>;

interface AdvancedSectionProps {
  collapsed: boolean;
  alternateName?: string;
  trackAndClick?: React.MouseEventHandler<HTMLDivElement>;
  children?: React.ReactNode;
  t: (key: string) => string;
  iconStyle?: string;
  iconPosition?: 'left' | 'right';
  tooltip?: string;
  tooltipDelay?: number;
  tooltipPlacement?: TooltipPosition;
  expandSection?: boolean;
  labelClassNames?: string;
}

export const AdvancedSection = ({
  collapsed,
  alternateName,
  trackAndClick,
  children,
  t,
  iconStyle,
  iconPosition = 'left',
  tooltip,
  tooltipDelay,
  tooltipPlacement,
  expandSection,
  labelClassNames,
}: AdvancedSectionProps) => {
  // Initial state: open if expandSection is true OR if not collapsed
  const [activeAccordion, setActiveAccordion] = React.useState<string>(expandSection || !collapsed ? '0' : '');

  // CRAB-53377: expandSection is a one-time trigger to force accordion open when validation
  // errors appear after mount. This useEffect is necessary because:
  // 1. We need local state for user-controlled open/close (not just props)
  // 2. But we also need to react to expandSection changing from false→true
  // 3. User can still close accordion after this opens it (no infinite loop)
  // Alternative considered: Fully controlled component, but that breaks user interaction
  React.useEffect(() => {
    if (expandSection) {
      setActiveAccordion('0');
    }
  }, [expandSection]);

  const determineIcon = (style: string | undefined) => {
    if (!_.isNil(style) && style === 'arrow') {
      if (activeAccordion !== '0') {
        return 'fa-chevron-right';
      }
      return 'fa-chevron-down';
    } else {
      if (activeAccordion !== '0') {
        return 'fa-plus';
      }
      return 'fa-minus';
    }
  };
  return (
    <div data-testid="advancedParameters">
      <Accordion
        value={activeAccordion}
        onItemSelect={(key) => setActiveAccordion(key)}
        accordionItems={[
          {
            value: '0',
            id: '0',
            trigger: (
              <div
                data-testid="panelControl"
                className={clsx('cursorPointer pt5 pb5 displayFlex tw:items-center', {
                  'sq-text-primary': !labelClassNames,
                })}
                onClick={trackAndClick}>
                {iconPosition === 'left' && <Icon icon={determineIcon(iconStyle)} testId="advancedParametersIcon" />}
                <span className={`${iconPosition === 'left' ? 'ml10' : ''} tw:text-left tw:flex-1`}>
                  <span className={labelClassNames}>{t(alternateName ? alternateName : 'ADVANCED')}</span>
                  {tooltip && (
                    <Icon
                      icon="fa-question-circle"
                      large={true}
                      tooltip={t(tooltip)}
                      tooltipDelay={tooltipDelay}
                      tooltipPlacement={tooltipPlacement}
                      extraClassNames="pl5"
                    />
                  )}
                </span>

                {iconPosition === 'right' && (
                  <Icon icon={determineIcon(iconStyle)} testId="advancedParametersIcon" extraClassNames="ml10" />
                )}
              </div>
            ),
            content: <>{children}</>,
          },
        ]}
      />
    </div>
  );
};
